/**
 * <video> replacement for backend-served media that sits behind JWT auth.
 *
 * Same problem as `AuthedImage`: the drama episode / segment file endpoints
 * require an `Authorization: Bearer` header (middleware/auth.py). A plain
 * `<video src>` is a browser GET that does NOT carry that header (and also
 * bypasses the axios baseURL, so it would miss the `/api/v1` prefix and the
 * Vite dev proxy). We fetch the bytes through the shared axios client — it
 * injects the token, the baseURL, and the 401-refresh interceptor — then play
 * the result as an object URL.
 *
 * Pass `path` as the axios-relative URL (exactly what `dramaApi.*Url(...)`
 * returns, e.g. "/drama-projects/{id}/episodes/1/file").
 */
import { useEffect, useRef, useState } from 'react'

import { api } from '@/lib/api'

interface AuthedVideoProps {
  path: string
  className?: string
  controls?: boolean
  autoPlay?: boolean
  /** `<video preload>`; ignored once we swap in the object URL but kept for parity. */
  preload?: 'none' | 'metadata' | 'auto'
  /** Mirrors native <video onError>: fired when the fetch fails (404/401/etc.). */
  onError?: () => void
  /**
   * Stream mode: ask the backend for a presigned URL (`?as_url=1`) and point the
   * <video> straight at S3 — native HTTP-Range streaming (instant start + seek)
   * instead of downloading the whole file as an authed blob. Use for large media
   * (episodes / segments). Falls back to the blob path if no URL is returned.
   */
  stream?: boolean
}

export function AuthedVideo({
  path,
  className,
  controls = true,
  autoPlay = false,
  preload = 'metadata',
  onError,
  stream = false,
}: AuthedVideoProps) {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  // Keep onError out of the effect deps (callers pass fresh closures each
  // render) so we don't refetch the video on every parent re-render.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let active = true
    let objectUrl: string | null = null
    setSrc(null)
    setFailed(false)

    // Blob path: fetch the bytes through the authed axios client and play as an
    // object URL. Needed for endpoints that stream JWT-protected bytes inline.
    const loadBlob = () => {
      api.client
        .get(path, { responseType: 'blob' })
        .then((res) => {
          if (!active) return
          objectUrl = URL.createObjectURL(res.data as Blob)
          setSrc(objectUrl)
        })
        .catch(() => {
          if (!active) return
          setFailed(true)
          onErrorRef.current?.()
        })
    }

    if (stream) {
      // Ask for a presigned URL and let <video> stream it straight from S3
      // (Range-capable) — no 100-300MB blob download, no gateway timeout.
      api.client
        .get(path, { params: { as_url: 1 } })
        .then((res) => {
          if (!active) return
          const u = (res.data as { url?: string } | null)?.url
          if (u) setSrc(u)
          else loadBlob()
        })
        .catch(() => {
          if (active) loadBlob()
        })
    } else {
      loadBlob()
    }

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path, stream])

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground text-xs ${className ?? ''}`}
      >
        视频加载失败
      </div>
    )
  }

  if (!src) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground text-xs animate-pulse ${className ?? ''}`}
      >
        加载中…
      </div>
    )
  }

  return (
    <video
      src={src}
      className={className}
      controls={controls}
      autoPlay={autoPlay}
      preload={preload}
      playsInline
    />
  )
}
